import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import Paragraph from 'components/atoms/Paragraph';
import Loading from 'components/atoms/Loading';
import Note from 'components/organisms/Note';
import Topbar from 'components/organisms/Topbar/Topbar';
import { ReactComponent as Plus } from 'assets/icons/plus.svg';
import { ReactComponent as ArrowRight } from 'assets/icons/arrow_right.svg';
import { database } from 'helpers/firebase';
import favicon from 'assets/icons/notes.ico';
import { HashLink } from 'react-router-hash-link';

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  min-height: 100vh;
  width: 100%;
  padding: 64px 0 0;
`;

const Row = styled.div`
  display: flex;
  flex-flow: row nowrap;
`;

const ListWrapper = styled.div`
  width: 300px;
  height: calc(100vh - 64px);
  border-right: 2px solid ${({ theme }) => theme.gray4};
`;

const List = styled.ul`
  display: flex;
  flex-flow: column-reverse nowrap;
  list-style: none;
  width: 100%;
`;

const ListItem = styled.li`
  width: 100%;
`;

const ListTitle = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  padding: 0 15px;
  height: 48px;
  border-bottom: 2px solid ${({ theme }) => theme.gray4};
  text-decoration: none;

  :hover {
    background-color: ${({ theme }) => theme.gray4};
  }

  ${({ isLast }) =>
    isLast
      ? css`
          ::before {
            height: ${isLast * 48 - 24}px !important;
            top: -${(isLast - 1) * 48}px !important;
          }
        `
      : null}

  ${({ isActive }) =>
    isActive
      ? css`
          background-color: ${({ theme }) => theme.gray4};
          cursor: default;
        `
      : null}

  ${Paragraph} {
    max-width: 100%;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
`;

const SubList = styled.ul`
  list-style: none;
  margin-left: 25px;

  ${ListTitle} {
    border-color: ${({ theme }) => theme.gray5};

    ::before {
      content: '';
      pointer-events: none;
      position: absolute;
      top: 0;
      left: -15px;
      width: 15px;
      height: 28px;
      border-left: 2px solid ${({ theme }) => theme.gray4};
      border-bottom: 2px solid ${({ theme }) => theme.gray4};
      border-bottom-left-radius: 5px;
    }
  }
`;

const StyledArrowRight = styled(ArrowRight)`
  fill: ${({ theme }) => theme.textSecondary};
  margin-left: -15px;
  margin-right: 5px;
  transform: rotate(${({ isActive }) => (isActive ? '90deg' : 0)});
  transition: transform 0.15s ease-in-out;
`;

const StyledPlus = styled(Plus)`
  fill: ${({ theme }) => theme.textSecondary};
  margin-left: -15px;
  margin-right: 5px;
`;

function Notes({ isAdmin }) {
  const [list, setList] = useState([]);
  const [subList, setSubList] = useState([]);
  const [topPosition, setTopPosition] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const { noteId } = useParams();

  useEffect(() => {
    const currentListItem = list.find((item) => item.key === noteId);

    if (currentListItem) {
      const currentSubList = [];
      currentListItem.value.split('\n').forEach((row) => {
        if (row[0] === '#' && row[1] === ' ') {
          currentSubList.push(row.replace('# ', '').trim());
        }
      });
      setSubList(currentSubList);
    }
  }, [list, noteId]);

  useEffect(() => {
    const ref = database.ref('/other/notes');

    ref.on('value', (data) => {
      if (data.exists()) {
        const newList = [];
        data.forEach((item) => {
          const { title, value } = item.val();
          const { key } = item;
          const isSubList = value.split('# ').length - 1;

          newList.push({ title, key, value, isSubList });
        });
        setList(newList);
        setIsLoading(false);
      }
    });
  }, []);

  return (
    <Wrapper>
      <Topbar title="Notatki" favicon={favicon} />
      <Row>
        <ListWrapper>
          <ListTitle as={Link} to="/notatki/nowa" isActive={noteId === 'nowa'}>
            <StyledPlus />
            <Paragraph>Nowa notatka</Paragraph>
          </ListTitle>
          <List>
            {isLoading ? (
              <Loading />
            ) : (
              list.map(({ title, key, isSubList }) => (
                <ListItem key={key}>
                  <ListTitle as={Link} to={`/notatki/${key}`} isActive={noteId === key}>
                    {isSubList ? <StyledArrowRight isActive={noteId === key} /> : null}
                    <Paragraph>{title}</Paragraph>
                  </ListTitle>
                  {noteId === key && subList.length ? (
                    <SubList>
                      {subList.map((item, i) => (
                        <ListItem key={i.toString()}>
                          <ListTitle
                            as={HashLink}
                            to={`/notatki/${key}#${item
                              .split(' ')
                              .join('-')
                              .split('(')
                              .join('')
                              .split(')')
                              .join('')}`}
                            isLast={i + 1}
                          >
                            <Paragraph>{item}</Paragraph>
                          </ListTitle>
                        </ListItem>
                      ))}
                    </SubList>
                  ) : null}
                </ListItem>
              ))
            )}
          </List>
        </ListWrapper>
        <Note noteId={noteId} setTopPosition={setTopPosition} />
      </Row>
    </Wrapper>
  );
}

Notes.propTypes = {
  isAdmin: PropTypes.bool,
};

Notes.defaultProps = {
  isAdmin: false,
};

export default Notes;
